/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import * as React from 'react';

import {
    CodeBlock,
    CodeBlockBody,
    CodeBlockExpandableFooter,
    CodeBlockLine,
    DEFAULT_SYNTAX,
    isAvailableLanguage,
    tokenizer,
    useSyntaxHighlighter,
} from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { CopyButton } from '../CopyButton';

export function DeveloperPlainCodeBlock(props: {
    code: string;
    syntax?: string;
    showLineNumbers?: boolean;
    expandable?: boolean;
    collapsedLineCount?: number;
    copyLabel?: string;
    copyIconOnly?: boolean;
    onCopy?: () => void;
    className?: string;
}): React.ReactElement {
    const {
        code,
        syntax,
        showLineNumbers = true,
        expandable = false,
        collapsedLineCount = 3,
        copyLabel = 'Copy',
        copyIconOnly = true,
        onCopy,
        className,
    } = props;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const highlighter = useSyntaxHighlighter();
    const resolvedSyntax = isAvailableLanguage(syntax) ? syntax : DEFAULT_SYNTAX;
    const [isSyntaxReady, setIsSyntaxReady] = React.useState(false);

    React.useEffect(() => {
        let isCancelled = false;

        async function loadSyntax() {
            if (!highlighter) {
                return;
            }

            try {
                if (!highlighter.getLoadedLanguages().includes(resolvedSyntax)) {
                    await highlighter.loadLanguage(resolvedSyntax);
                }
            } catch (_) {
                // Fallback to plain tokenization if the syntax cannot be loaded.
            }

            if (!isCancelled) {
                setIsSyntaxReady(true);
            }
        }

        setIsSyntaxReady(false);
        void loadSyntax();

        return () => {
            isCancelled = true;
        };
    }, [highlighter, resolvedSyntax]);

    const highlighted = React.useMemo(() => {
        if (!highlighter || !isSyntaxReady) {
            return null;
        }

        return tokenizer(highlighter, code, resolvedSyntax);
    }, [code, highlighter, isSyntaxReady, resolvedSyntax]);

    const codeLines = (highlighted?.tokens ?? code.split('\n')).map((line, lineIndex) => {
        const lineNumber = showLineNumbers ? lineIndex + 1 : undefined;

        if (Array.isArray(line)) {
            return (
                <CodeBlockLine
                    key={lineIndex}
                    number={lineNumber}
                    tokens={line
                        .map(({ color, content, fontStyle }) =>
                            color || fontStyle ? { color, content, fontStyle } : null
                        )
                        .filter(filterOutNullable)}
                />
            );
        }

        return (
            <CodeBlockLine key={lineIndex} number={lineNumber}>
                {line}
            </CodeBlockLine>
        );
    });
    const shouldExpand = expandable && codeLines.length > collapsedLineCount;

    return (
        <CodeBlock
            showLineNumbers={showLineNumbers}
            showLineGutter={showLineNumbers}
            overflow="wrap"
            expandable={shouldExpand}
            collapsedLineCount={collapsedLineCount}
            isExpanded={isExpanded}
            numberOfLines={codeLines.length}
            className={clsx(!showLineNumbers && 'px-5', className)}
        >
            <CodeBlockBody>{codeLines}</CodeBlockBody>
            <div
                className="pointer-events-none absolute top-2 right-2 z-10"
                contentEditable={false}
            >
                <CopyButton
                    value={code}
                    label={copyLabel}
                    size="small"
                    kind="blank"
                    iconOnly={copyIconOnly}
                    onCopy={onCopy}
                    className="pointer-events-auto"
                />
            </div>
            {shouldExpand ? <CodeBlockExpandableFooter onChange={setIsExpanded} /> : null}
        </CodeBlock>
    );
}
